import React, { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";

import Button from "@components/Button";
import DatePickerInput from "@components/DatePicker/DatePickerInput";
import Dialog from "@components/Dialog";
import Input from "@components/Input";
import SizedBox from "@components/SizedBox";
import { Row } from "@components/Flex";
import { useLang, useMoment } from "@src/hooks/useLang";

interface IProps {
  visible: boolean;
  initialFrom?: Date;
  initialTo?: Date;
  onClose?: () => void;
  onSubmit: (from: Date, to: Date) => void;
}

const EditSeasonDialog: React.FC<IProps> = ({
  onSubmit,
  initialFrom,
  initialTo,
  ...rest
}) => {
  const lang = useLang();
  const moment = useMoment();

  const seasonName = (start: Date, end: Date): string => {
    const from = moment(start);
    const to = moment(end);

    if (from.year() !== to.year()) {
      return from.format("YYYY MMM") + " - " + to.format("YYYY MMM");
    }
    if (from.month() !== to.month()) {
      return from.format("YYYY MMM") + " - " + to.format("MMM");
    }
    return from.format("YYYY MMM DD") + " - " + to.format("DD");
  };

  const [from, setStartDate] = useState<Date | null>(initialFrom ?? null);
  const [to, setEndDate] = useState<Date | null>(initialTo ?? null);

  // Reset the fields whenever the dialog opens for a different season.
  useEffect(() => {
    if (rest.visible) {
      setStartDate(initialFrom ?? null);
      setEndDate(initialTo ?? null);
    }
  }, [rest.visible, initialFrom, initialTo]);

  const disabled = from == null || to == null || from.getTime() >= to.getTime();

  return (
    <Dialog
      style={{ width: 336 }}
      title={lang.seasons.editDialogTitle}
      {...rest}
    >
      <Input
        value={(from && to) ? seasonName(from!, to!) : ""}
        disabled={true}
        label={lang.seasons.nameTitle}
      />
      <SizedBox height={16} />
      <Row>
        <DatePickerInput value={from} onChange={setStartDate} />
        <SizedBox width={16} />
        <DatePickerInput value={to} onChange={setEndDate} />
      </Row>
      <SizedBox height={24} />
      <Row>
        <Button kind="outline" onClick={rest.onClose}>
          {lang.seasons.cancelButton}
        </Button>
        <SizedBox width={16} />
        <Button onClick={() => onSubmit(from!, to!)} disabled={disabled}>
          {lang.seasons.saveButton}
        </Button>
      </Row>
    </Dialog>
  );
};
export default observer(EditSeasonDialog);
